import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, CheckCircle } from 'lucide-react';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { UnitType } from '@/types';
import { doc, getDoc, collection, query, where, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import {
  parseDefaultCreditBalanceOnUnitCreate,
  requestUnitMembership,
} from '@masonic-tools/shared-auth';
import Swal from 'sweetalert2';

export default function CreateUnitPage() {
  const [sendingVerification, setSendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [unitName, setUnitName] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { currentUser, refreshUserProfile, sendVerificationEmail } = useAuth();
  const navigate = useNavigate();

  // Get selected unit type details
  const selectedUnitType = unitTypes.find(ut => ut.id === selectedTypeId);
  const entityName = selectedUnitType?.entityName || 'Unit';
  const requireNumber = selectedUnitType?.fieldConfig?.requireNumber ?? true;
  const useNumberForUniqueness = selectedUnitType?.fieldConfig?.useNumberForUniqueness ?? true;

  // Fetch unit types
  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        let types: UnitType[] = [];
        
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          console.log('System settings data:', data);
          types = data.unitTypes || [];
          console.log('Unit types from DB:', types);
        } else {
          console.log('System settings document does not exist');
        }
        
        console.log('Final unit types:', types);
        setUnitTypes(types);
        // Auto-select first type (usually Craft Unit)
        if (types.length > 0) {
          setSelectedTypeId(types[0].id);
          console.log('Selected type ID:', types[0].id);
        }
      } catch (error) {
        console.error('Error fetching unit types:', error);
      }
    };

    fetchUnitTypes();
  }, []);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Check for email verification status periodically
  useEffect(() => {
    if (!currentUser?.emailVerified && verificationSent) {
      const interval = setInterval(async () => {
        setCheckingVerification(true);
        await currentUser?.reload();
        await refreshUserProfile();
        setCheckingVerification(false);
        
        if (currentUser?.emailVerified) {
          showSuccess('Email verified successfully! You can now create your unit.');
        }
      }, 3000); // Check every 3 seconds

      return () => clearInterval(interval);
    }
  }, [currentUser, verificationSent, refreshUserProfile]);

  const handleSendVerification = async () => {
    setSendingVerification(true);
    try {
      if (sendVerificationEmail) {
        await sendVerificationEmail('/unit/create');
        setVerificationSent(true);
        setCountdown(30);
        showSuccess('Verification email sent! Please check your inbox and spam folder.');
      }
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      showError(error?.message || 'Failed to send verification email. Please try again.');
    } finally {
      setSendingVerification(false);
    }
  };

  const handleRequestMembership = async (existingUnitId: string, existingUnitName: string, attemptedName: string, attemptedNumber: string) => {
    if (!currentUser?.email) return;

    try {
      await requestUnitMembership(
        db,
        existingUnitId,
        existingUnitName,
        currentUser.uid,
        currentUser.email,
        attemptedName,
        attemptedNumber,
        'duplicate_attempt'
      );

      showSuccess('Membership request sent successfully! The unit secretary will review your request.');
      navigate('/dashboard');
    } catch (error: unknown) {
      console.error('Error creating membership request:', error);
      const message = error instanceof Error ? error.message : 'Failed to send membership request. Please try again.';
      showError(message);
    }
  };

  const handleReportUnauthorized = async (existingUnitId: string, existingUnitName: string, attemptedName: string, attemptedNumber: string) => {
    if (!currentUser) return;

    const { value: reason } = await Swal.fire({
      title: 'Report Unauthorized Creation',
      html: `
        <p class="mb-4">You're reporting that "${existingUnitName}" was created without proper authorization.</p>
        <p class="mb-2">Please provide details about why you believe this was created without authorization:</p>
      `,
      input: 'textarea',
      inputPlaceholder: 'Enter your reason for reporting...',
      inputAttributes: {
        'aria-label': 'Reason for reporting',
      },
      showCancelButton: true,
      confirmButtonText: 'Submit Report',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value || value.trim().length < 10) {
          return 'Please provide at least 10 characters explaining your report';
        }
        return null;
      },
    });

    if (reason) {
      try {
        // Create a report in Firestore
        await addDoc(collection(db, 'unitReports'), {
          reportedUnitId: existingUnitId,
          reportedUnitName: existingUnitName,
          reporterId: currentUser.uid,
          reporterEmail: currentUser.email,
          reason: reason,
          status: 'pending',
          createdAt: Timestamp.now(),
          attemptedUnitName: attemptedName,
          attemptedUnitNumber: attemptedNumber,
        });

        showSuccess('Report submitted successfully! Our team will review this matter.');
        navigate('/dashboard');
      } catch (error) {
        console.error('Error submitting report:', error);
        showError('Failed to submit report. Please try again.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      showError('You must be logged in to create a unit');
      return;
    }

    // Check email verification
    if (!currentUser.emailVerified) {
      showError('You must verify your email address before creating a unit.');
      return;
    }

    if (!selectedTypeId) {
      showError('Please select a unit type');
      return;
    }

    if (!unitName.trim() || (requireNumber && !unitNumber.trim())) {
      showError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      // Check for duplicates within the same type
      const unitsRef = collection(db, 'units'); // Still using units collection for now
      const duplicateQuery = useNumberForUniqueness
        ? query(
            unitsRef,
            where('name', '==', unitName.trim()),
            where('number', '==', unitNumber.trim()),
            where('typeId', '==', selectedTypeId)
          )
        : query(
            unitsRef,
            where('name', '==', unitName.trim()),
            where('typeId', '==', selectedTypeId)
          );

      const duplicateSnapshot = await getDocs(duplicateQuery);

      if (!duplicateSnapshot.empty) {
        const existingUnit = duplicateSnapshot.docs[0].data();
        const statusText = existingUnit.reviewStatus === 'pending' ? ' (currently under review)' : '';
        
        const result = await Swal.fire({
          title: `${entityName} Already Exists`,
          html: `
            <div class="text-left">
              <p class="mb-4">A ${entityName.toLowerCase()} with the same name and number already exists in the ${selectedUnitType?.name} category${statusText}:</p>
              <div class="bg-gray-100 p-3 rounded mb-4">
                <p class="font-semibold">${existingUnit.name}</p>
                <p class="text-sm text-gray-600">${existingUnit.unitNumber || existingUnit.number}</p>
              </div>
              <p class="mb-3">Would you like to:</p>
            </div>
          `,
          icon: 'warning',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Request Membership',
          denyButtonText: 'Report Unauthorized Creation',
          cancelButtonText: 'Go Back',
          confirmButtonColor: '#1E3A5F',
          denyButtonColor: '#dc2626',
          cancelButtonColor: '#6c757d',
        });

        if (result.isConfirmed) {
          await handleRequestMembership(duplicateSnapshot.docs[0].id, existingUnit.name, unitName, unitNumber);
        } else if (result.isDenied) {
          await handleReportUnauthorized(duplicateSnapshot.docs[0].id, existingUnit.name, unitName, unitNumber);
        }
        setSubmitting(false);
        return;
      }

      const platformSnap = await getDoc(doc(db, 'systemSettings', 'platform'));
      const startingCredits = parseDefaultCreditBalanceOnUnitCreate(
        platformSnap.exists() ? (platformSnap.data() as Record<string, unknown>) : undefined,
      );
      const startingCreditFields =
        startingCredits > 0
          ? { creditBalance: startingCredits, creditBalanceUpdatedAt: Timestamp.now() }
          : {};

      // Create the unit
      await addDoc(collection(db, 'units'), {
        name: unitName.trim(),
        unitNumber: unitNumber.trim(),
        number: unitNumber.trim(),
        typeId: selectedTypeId,
        createdBy: currentUser.uid,
        createdByEmail: currentUser.email,
        createdAt: Timestamp.now(),
        members: [],
        reviewStatus: 'pending',
        reviewData: {
          submittedAt: Timestamp.now(),
          submittedBy: currentUser.uid,
          submittedByEmail: currentUser.email,
        },
        subscription: {
          status: 'trial',
          billingEmail: currentUser.email,
        },
        settings: {},
        ...startingCreditFields,
      });

      await Swal.fire({
        title: `${entityName} Submitted for Review`,
        html: `
          <div class="text-left">
            <p class="mb-4">Your ${entityName.toLowerCase()} has been successfully submitted for review!</p>
            <div class="bg-blue-50 p-4 rounded-lg mb-4">
              <p class="font-semibold text-blue-900">${unitName}</p>
              <p class="text-sm text-blue-700">${unitNumber}</p>
              <p class="text-xs text-blue-600 mt-1">${selectedUnitType?.name}</p>
            </div>
            <p class="mb-3">What happens next:</p>
            <ul class="list-disc pl-5 space-y-2 text-sm">
              <li>Our administrators will review your ${entityName.toLowerCase()} application</li>
              <li>You'll receive an email notification once it's been reviewed</li>
              <li>You can check the status on your dashboard</li>
              <li>If approved, you'll gain full access to the ${entityName.toLowerCase()}</li>
            </ul>
            <p class="mt-4 text-sm text-gray-600">This usually takes 24-48 hours.</p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'Go to Dashboard',
        confirmButtonColor: '#1E3A5F',
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating unit:', error);
      showError(`Failed to create ${entityName.toLowerCase()}. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  // Email verification component
  const verificationComponent = (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Verify Your Email</CardTitle>
            <CardDescription>
              Before creating your unit, we need to verify your email address.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-blue-100 p-4">
                  <Mail className="h-12 w-12 text-blue-600" />
                </div>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  We'll send a verification link to:
                </p>
                <p className="font-semibold text-lg">{currentUser?.email}</p>
              </div>

              {!verificationSent ? (
                <Button
                  onClick={handleSendVerification}
                  disabled={sendingVerification}
                  className="w-full"
                  size="lg"
                >
                  {sendingVerification ? 'Sending...' : 'Send Verification Email'}
                </Button>
              ) : (
                <div className="space-y-4">
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      <p className="font-semibold mb-1">Verification email sent!</p>
                      <p className="text-sm">
                        Please check your inbox and spam folder. Click the link in the email to verify your address.
                      </p>
                    </AlertDescription>
                  </Alert>

                  {checkingVerification && (
                    <p className="text-sm text-gray-500 animate-pulse">
                      Checking verification status...
                    </p>
                  )}

                  <Button
                    onClick={handleSendVerification}
                    disabled={countdown > 0 || sendingVerification}
                    variant="outline"
                    className="w-full"
                  >
                    {sendingVerification
                      ? 'Sending...'
                      : countdown > 0
                      ? `Resend in ${countdown}s`
                      : 'Resend Verification Email'}
                  </Button>

                  <div className="pt-4 border-t">
                    <p className="text-xs text-gray-500 text-center">
                      After verifying your email, you'll be redirected back to this page automatically.
                    </p>
                  </div>
                </div>
              )}

              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="w-full"
              >
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );

  // Show message if no unit types are configured
  if (unitTypes.length === 0) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Unit Types Not Configured</CardTitle>
              <CardDescription>
                Before creating a unit, unit types must be configured in the platform settings.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center space-y-6">
                <div className="flex justify-center">
                  <div className="rounded-full bg-amber-100 p-4">
                    <Mail className="h-12 w-12 text-amber-600" />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Alert>
                    <AlertDescription>
                      <p className="font-semibold mb-2">Unit types are required</p>
                      <p className="text-sm mb-3">
                        The platform administrator needs to configure unit types (such as Craft Lodge, Royal Arch Chapter, etc.) before units can be created.
                      </p>
                      <p className="text-sm">
                        Please contact your platform administrator to set up unit types in the platform settings.
                      </p>
                    </AlertDescription>
                  </Alert>
                </div>

                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="default"
                  className="w-full"
                >
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Show email verification if needed
  if (!currentUser?.emailVerified) {
    return verificationComponent;
  }

  // Main create form
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Create new Application</CardTitle>
            <CardDescription>
              Set up your Unit/Chapter to start managing events. You'll be assigned as the Secretary.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="unitType">Type *</Label>
                <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
                  <SelectTrigger id="unitType">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {unitTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name} ({type.entityName})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Select the type of masonic unit you want to create
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="unitName">Name *</Label>
                <Input
                  id="unitName"
                  type="text"
                  placeholder={`e.g., Veritas ${entityName}`}
                  value={unitName}
                  onChange={(e) => setUnitName(e.target.value)}
                  required
                />
              </div>

              {requireNumber && (
                <div className="space-y-2">
                  <Label htmlFor="unitNumber">Number *</Label>
                  <Input
                    id="unitNumber"
                    type="text"
                    placeholder="e.g., No. 1234"
                    value={unitNumber}
                    onChange={(e) => setUnitNumber(e.target.value)}
                    required
                  />
                </div>
              )}

              <div className="pt-2">
                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={submitting || !selectedTypeId}
                >
                  {submitting ? `Creating ${entityName}...` : `Create ${entityName}`}
                </Button>
              </div>

              <div className="space-y-2 text-center">
                <p className="text-sm text-muted-foreground">
                  Looking to join an existing lodge or chapter?{' '}
                  <button
                    type="button"
                    className="font-medium underline"
                    onClick={() => navigate('/unit/join')}
                  >
                    Request membership
                  </button>
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
